import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowLeft, Loader2, User, MessageCircle, ArrowRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { buildWhatsAppUrl } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://moveonshifter.in";

function upsertMeta(selector, attr, name, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function upsertJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/blog/${slug}`)
      .then(({ data }) => {
        setPost(data);
        const url = `${SITE_URL}/blog/${data.slug}`;
        const img = data.featured_image?.startsWith("http") ? data.featured_image : `${SITE_URL}${data.featured_image}`;
        const pageTitle = `${data.title} | Move On Packers & Movers`;

        document.title = pageTitle;
        upsertMeta('meta[name="description"]', "name", "description", data.meta_description);
        upsertLink("canonical", url);

        // Open Graph
        upsertMeta('meta[property="og:type"]', "property", "og:type", "article");
        upsertMeta('meta[property="og:title"]', "property", "og:title", data.title);
        upsertMeta('meta[property="og:description"]', "property", "og:description", data.meta_description);
        upsertMeta('meta[property="og:url"]', "property", "og:url", url);
        upsertMeta('meta[property="og:image"]', "property", "og:image", img);
        upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "Move On Packers & Movers");

        // Twitter
        upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
        upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", data.title);
        upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", data.meta_description);
        upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", img);

        // JSON-LD Article
        upsertJsonLd("article", {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: data.title,
          description: data.meta_description,
          image: [img],
          datePublished: data.published_at,
          dateModified: data.published_at,
          author: { "@type": "Organization", name: data.author || "Move On Packers & Movers" },
          publisher: {
            "@type": "Organization",
            name: "Move On Packers & Movers",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          keywords: (data.tags || []).join(", "),
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    return () => {
      // Clean article-specific JSON-LD when leaving the page
      const el = document.head.querySelector('script[data-jsonld="article"]');
      if (el) el.remove();
    };
  }, [slug]);

  if (loading) return <div className="py-24 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading...</div>;
  if (notFound || !post) return (
    <div className="py-24 max-w-2xl mx-auto text-center px-4">
      <h1 className="font-[Outfit] text-3xl font-bold text-[#081537]">Post not found</h1>
      <p className="mt-3 text-slate-600">The article you&rsquo;re looking for doesn&rsquo;t exist or was moved.</p>
      <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#081537] font-semibold hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to Blog
      </Link>
    </div>
  );

  const dt = post.published_at ? parseISO(typeof post.published_at === "string" ? post.published_at : post.published_at) : null;

  return (
    <div>
      {/* Header */}
      <section className="pt-12 pb-6 border-b border-slate-100 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#081537] mb-6" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3">
            {post.tags?.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-[#081537]/5 text-[#081537] font-semibold">{t}</span>
            ))}
          </div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#081537] leading-tight text-balance">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-500">
            {dt && <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {format(dt, "MMMM d, yyyy")}</span>}
            <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
          </div>
        </div>
      </section>

      {/* Featured image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
        <div className="aspect-[16/8] overflow-hidden rounded-2xl bg-slate-100 mt-6">
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Body */}
      <article className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="prose-move"
            data-testid="blog-content"
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />

          {/* Inline CTA */}
          <div className="mt-14 bg-[#081537] text-white rounded-2xl p-8 sm:p-10">
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-yellow-400">Private Consultation</div>
            <h3 className="font-[Outfit] text-2xl sm:text-3xl font-bold mt-2">Get a survey-based moving quote, not a guess.</h3>
            <p className="mt-3 text-slate-300 max-w-xl">Tell us about your move and we&rsquo;ll come back with a transparent itemized quotation within 2 hours.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/book" data-testid="blog-book-cta" className="inline-flex items-center gap-2 bg-white text-[#081537] rounded-full px-6 py-3 font-semibold hover:bg-slate-100 transition">
                Book a Private Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={buildWhatsAppUrl("Hi, I read your pricing guide. I'd like a quote.")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white rounded-full px-6 py-3 font-semibold transition"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
